import { BaseGQLService } from '../../../common/services/BaseGQLService';
import { IntegrationConnectionResultMapper } from '../../mappers/IntegrationConnectionResultMapper';
import { IntegrationConnectionResult } from '../../models/IntegrationConnectionResult';
import { TaniumMapper } from '../mappers/TaniumMapper';
import { Tanium } from '../models/Tanium';
import { ProjectTaniumMapper } from '../mappers/ProjectTaniumMapper';
import { ProjectTanium } from '../models/ProjectTanium';
import { ModelType } from '../../../common/utils/GQLIdEncoder';
import { InvalidUrlError } from '../../../common/errors/errors';

export const PROJECT_TANIUM_FIELDS = `
computerGroup
status
todoCount
endpointsCount
completedAt
`;

export const UPDATE_TANIUM_INTEGRATION = `
mutation($url: String!, $accessToken: String!, $trustedCaCertificate: String, $maxConcurrentRetrievals: Int, $requestRate: Int) {
  updateTaniumIntegration(input: {
    url: $url,
    accessToken: $accessToken,
    trustedCaCertificate: $trustedCaCertificate,
    maxConcurrentRetrievals: $maxConcurrentRetrievals,
    requestRate: $requestRate
  }) {
    taniumIntegration {
      url
      accessToken
      trustedCaCertificate
      maxConcurrentRetrievals
      requestRate
    }
  }
}
`;

export const CHECK_TANIUM_INTEGRATION = `
mutation($url: String!, $accessToken: String!, $trustedCaCertificate: String) {
  checkTaniumIntegration(input: { url: $url, accessToken: $accessToken, trustedCaCertificate: $trustedCaCertificate }) {
    taniumConnectionResult {
      isSuccessful
      reason
    }
  }
}
`;

export const UNLINK_TANIUM_INTEGRATION = `
mutation {
  unlinkTaniumIntegration(input: {}) {
    __typename
  }
}
`;

export const PROJECT_TANIUM_QUERY = `
query($projectId: ID!) {
  project: node(id: $projectId) {
      ... on Project {
          tanium {
              ${PROJECT_TANIUM_FIELDS}
          }
      }
  }
}
`;

export const PROJECT_TANIUM_LOG_QUERY = `
query($projectId: ID!) {
  project: node(id: $projectId) {
      ... on Project {
          tanium {
              log
          }
      }
  }
}
`;

export const UPDATE_PROJECT_TANIUM_COMPUTER_GROUP = `
mutation(
  $projectId: String!,
  $computerGroup: String!
) {
  updateProjectTanium(input: {
      projectId: $projectId
      computerGroup: $computerGroup
  }) {
      projectTanium {
          ${PROJECT_TANIUM_FIELDS}
      }
  }
}
`;

export const CHECK_TANIUM_COMPUTER_GROUP = `
mutation(
  $computerGroup: String!,
) {
  checkTaniumComputerGroup(input: {
      computerGroup: $computerGroup
  }) {
      taniumConnectionResult {
          isSuccessful
          reason
      }
  }
}
`;

export const START_TANIUM_RETRIEVAL = `
mutation(
  $projectId: String!,
) {
  startTaniumRetrieval(input: {
      projectId: $projectId
  }) {
      result {
        ${PROJECT_TANIUM_FIELDS}
      }
  }
}
`;

export const STOP_TANIUM_RETRIEVAL = `
mutation(
  $projectId: String!,
) {
  stopTaniumRetrieval(input: {
      projectId: $projectId
  }) {
      result {
          computerGroup
          status
      }
  }
}
`;

export const UNLINK_PROJECT_TANIUM = `
mutation(
  $projectId: ID!,
) {
  unlinkProjectTanium(input: {
      projectId: $projectId
  }) {
      clientMutationId
  }
}
`;

export type UpdateTaniumIntegrationArgs = {
  url: string;
  accessToken: string;
  trustedCaCertificate: string;
  maxConcurrentRetrievals: number;
  requestRate: number;
};

export type CheckTaniumIntegrationArgs = Pick<UpdateTaniumIntegrationArgs, 'url' | 'accessToken' | 'trustedCaCertificate'>;

export class TaniumRepository extends BaseGQLService {
  private readonly testConnectionResultMapper = new IntegrationConnectionResultMapper();
  private readonly taniumMapper = new TaniumMapper();
  private readonly projectTaniumMapper = new ProjectTaniumMapper();

  async checkIntegrationConnection(args: CheckTaniumIntegrationArgs): Promise<IntegrationConnectionResult> {
    return this.mutate(CHECK_TANIUM_INTEGRATION, args, (data) =>
      this.testConnectionResultMapper.mapGQLModel(data.checkTaniumIntegration.taniumConnectionResult)
    );
  }

  async updateIntegration(args: UpdateTaniumIntegrationArgs): Promise<Tanium> {
    return this.mutate(UPDATE_TANIUM_INTEGRATION, args, (data) => this.taniumMapper.mapGQLModel(data.updateTaniumIntegration.taniumIntegration));
  }

  async unlinkIntegration(): Promise<void> {
    return this.mutate(UNLINK_TANIUM_INTEGRATION, {});
  }

  async getProjectTanium(projectId: number): Promise<ProjectTanium | null> {
    return this.query(PROJECT_TANIUM_QUERY, { projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId) }, (data) => {
      if (!data.project) {
        throw new InvalidUrlError(`Invalid request: could not find Project ${projectId}`);
      }
      const projectTanium = data.project.tanium;
      if (projectTanium) {
        return this.projectTaniumMapper.mapGQLModel(projectTanium);
      }
      return null;
    });
  }

  async updateProjectTaniumComputerGroup(projectId: number, computerGroup: string): Promise<ProjectTanium> {
    return this.mutate(
      UPDATE_PROJECT_TANIUM_COMPUTER_GROUP,
      { projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId), computerGroup },
      (data: any) => {
        return this.projectTaniumMapper.mapGQLModel(data.updateProjectTanium.projectTanium);
      }
    );
  }

  async checkTaniumComputerGroup(computerGroup: string): Promise<IntegrationConnectionResult> {
    return this.mutate(CHECK_TANIUM_COMPUTER_GROUP, { computerGroup }, (data: any) => {
      return this.testConnectionResultMapper.mapGQLModel(data.checkTaniumComputerGroup.taniumConnectionResult);
    });
  }

  async startTaniumRetrieval(projectId: number): Promise<ProjectTanium> {
    return this.mutate(START_TANIUM_RETRIEVAL, { projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId) }, (data: any) => {
      return this.projectTaniumMapper.mapGQLModel(data.startTaniumRetrieval.result);
    });
  }

  async stopTaniumRetrieval(projectId: number): Promise<ProjectTanium> {
    return this.mutate(STOP_TANIUM_RETRIEVAL, { projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId) }, (data: any) => {
      return this.projectTaniumMapper.mapGQLModel(data.stopTaniumRetrieval.result);
    });
  }

  async unlinkProjectTanium(projectId: number): Promise<void> {
    return this.mutate(UNLINK_PROJECT_TANIUM, {
      projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId),
    });
  }

  async getProjectTaniumLog(projectId: number): Promise<string | null> {
    return this.query(PROJECT_TANIUM_LOG_QUERY, { projectId: this.gqlIdEncoder.encode(ModelType.Project, projectId) }, (data: any) => {
      return data.project.tanium.log;
    });
  }
}
